import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useBestEffortPiano } from '../../core/hooks/useBestEffortPiano';
import { PIANO_EVENTS } from '../../core/constants/pianoEvents';
import { usePopupBanner } from './usePopupBanner';
import { usePopupBannerArtwork } from './usePopupBannerArtwork';
import { getPopupBannerShown, markPopupBannerShown } from './popupBannerSession';
import { getOsatShown } from '../../core/constants/osat';
import { openPopupBannerCta } from './openPopupBannerCta';
import type { PopupBannerCta } from './popupBanner.types';

const PopupBannerDialog = lazy(() => import('./PopupBannerDialog'));

/**
 * Owns the banner's lifecycle: decide, preload, open once, record the dismissal, tag.
 *
 * Renders nothing until the artwork has actually loaded. On a load failure it never opens and
 * records no dismissal (FE-17), so the user still gets the banner on a later visit.
 *
 * The session flag is marked when the dialog opens, not when the banner resolves — a banner whose
 * artwork failed has not been shown and must not consume the one showing.
 */
const PopupBannerHost = (): React.JSX.Element | null => {
  const { banner, dismiss } = usePopupBanner();
  const artwork = usePopupBannerArtwork(banner);
  const { trackView, trackClick } = useBestEffortPiano();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOpen || !banner || artwork !== 'ready') return;
    if (getOsatShown() || getPopupBannerShown()) return;
    markPopupBannerShown();
    setIsOpen(true);
    trackView(PIANO_EVENTS.BANNER.VIEW);
  }, [isOpen, banner, artwork, trackView]);

  const handleClose = useCallback((): void => {
    dismiss();
    trackClick(PIANO_EVENTS.BANNER.CLOSE);
    setIsOpen(false);
  }, [dismiss, trackClick]);

  const handleCtaClick = useCallback(
    (cta: PopupBannerCta): void => {
      dismiss();
      trackClick(PIANO_EVENTS.BANNER.CLICK_CTA);
      openPopupBannerCta(cta, navigate);
      setIsOpen(false);
    },
    [dismiss, trackClick, navigate],
  );

  if (!isOpen || !banner) return null;

  return (
    <Suspense fallback={null}>
      <PopupBannerDialog banner={banner} onClose={handleClose} onCtaClick={handleCtaClick} />
    </Suspense>
  );
};

export default PopupBannerHost;
